import { radius, spacer, colors, fontSize, fontWeight } from 'settings/style';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Button } from 'components/elements';

/** antd v5's default `colorLink`, i.e. the color the card link already renders at. */
const cardLinkColor = '#1677ff';

export const StyledWrapper = styled(Link)`
  display: flex;
  flex-flow: column;
  border-radius: 12px;
  flex: 1;
  height: 100%;
  background-color: ${colors.white};
  box-shadow: 0 2px 16px 0 rgba(120, 130, 140, 0.1);
  width: 100%;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
  border: 1px solid ${colors.cardBorder};

  /* The card is an <a>, and antd tints every anchor from colorLink to
     colorLinkHover on hover. Hold the color steady so anything inheriting it
     (e.g. the group icon) looks identical in every state. */
  color: ${cardLinkColor};

  &:hover,
  &:focus,
  &:active {
    color: ${cardLinkColor};
  }

  &:hover {
    box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);
  }
`;

export const StyledImgWrap = styled.div<{ url?: string }>`
  position: relative;
  background: ${({ url }) => (url ? `url(${url})` : '#e5e7eb')};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  height: 200px;
  flex-shrink: 0;
  border: 1px solid ${colors.cardBorder};
`;

export const StyledDateBadge = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 60px;
  text-align: center;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
`;

export const StyledBadgeRow = styled.div`
  position: absolute;
  left: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: calc(100% - 16px);
`;

export const StyledDateDay = styled.div`
  font-size: 1.65rem;
  font-weight: ${fontWeight.bolder};
  color: #1a2233;
  line-height: 1;
  letter-spacing: -0.01em;
`;

export const StyledDateMonthYear = styled.div`
  font-size: 0.65rem;
  font-weight: ${fontWeight.bold};
  color: #7b8a9a;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 3px;
`;

export const StyledImg = styled.img`
  object-fit: cover;
  border-top-right-radius: ${radius.lg};
  border-top-left-radius: ${radius.lg};
`;

export const StyledContent = styled.div`
  padding: 18px 20px 20px;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const StyledBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  border-bottom-right-radius: ${radius.lg};
  border-bottom-left-radius: ${radius.lg};
  margin-top: auto;
`;

export const StyledText = styled.span`
  display: inline-flex;
  max-width: 256px;
`;

export const StyledBottomContent = styled.div`
  display: flex;
  flex-flow: column;
  margin-right: ${spacer.sm};
`;

export const StyledBottomWrap = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  i {
    color: #8c9aaa;
    font-size: ${fontSize.sm};
    margin-right: 6px;
  }
`;
export const StyledBottomText = styled.span`
  display: inline-flex;
  color: #6b7a8d;
  font-size: ${fontSize.sm};
  margin-left: 10px;
`;
export const StyledBottomCount = styled.span`
  display: inline-flex;
  color: ${colors.primary};
  margin-left: ${spacer.md};
`;

export const StyledMembersBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: ${fontWeight.bold};
  border: 1px solid ${colors.cardBorder};
  background-color: ${colors.white};
  color: ${colors.black};
`;

export const StyledImgTitle = styled.h4`
  margin: 0 0 10px;
  font-size: 1.1rem;
  font-weight: ${fontWeight.bold};
  font-family: 'Cormorant Garamond';
  color: #1a2233;
  line-height: 1.35;
`;

/** Title + organizer icon actions (fixed slot keeps card height consistent). */
export const StyledTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  min-height: 40px;

  ${StyledImgTitle} {
    margin: 0;
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const StyledActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  flex-shrink: 0;
  min-width: 64px;
  min-height: 32px;
`;

export const StyledCardIconButton = styled(Button)`
  flex-shrink: 0;
  border: none;
  width: 32px;
  height: 32px !important;
  min-width: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/** @deprecated Use StyledCardIconButton */
export const StyledEditIconButton = StyledCardIconButton;

export const StyledDescription = styled.div`
  margin: 10px 0 0;
  color: #7b8a9a;
  font-size: ${fontSize.sm};
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  p {
    margin: 0;
    display: inline;
  }
`;
